import { Tooltip } from 'antd';
import { SmileOutlined } from '@ant-design/icons';
import { MAX_REACTIONS_PER_USER, type ReactionSummary } from '@lens/shared';
import { ReactionPicker } from './ReactionPicker.tsx';
import styles from './ReactionBar.module.css';

interface ReactionBarProps {
  reactions: ReactionSummary[];
  /** member+ может ставить/снимать; наблюдатель (viewer) только видит. */
  canReact: boolean;
  /** Тоггл emoji — родитель решает add/remove по текущему reactedByMe. */
  onToggle: (emoji: string) => void;
  /** 'sm' — компактный вариант для комментариев. */
  size?: 'sm' | 'md';
  /** Показывать ли встроенную кнопку добавления (в комментах пикер вынесен в
      hover-действия, поэтому здесь его выключают). */
  showAdd?: boolean;
  /** Блокировка на время запроса. */
  disabled?: boolean;
}

/**
 * Ряд чипов-реакций с счётчиками. Опционально — кнопка добавления (пикер).
 * Презентационный компонент: состояние и запросы держит родитель.
 */
export function ReactionBar({
  reactions,
  canReact,
  onToggle,
  size = 'md',
  showAdd = true,
  disabled = false,
}: ReactionBarProps): JSX.Element | null {
  const visible = reactions.filter((r) => r.count > 0);
  const myCount = visible.filter((r) => r.reactedByMe).length;
  const atLimit = myCount >= MAX_REACTIONS_PER_USER;

  const renderAdd = canReact && showAdd;
  // Нечего показывать: ни чипов, ни кнопки добавления.
  if (visible.length === 0 && !renderAdd) return null;

  const handleToggle = (emoji: string) => {
    if (!canReact || disabled) return;
    onToggle(emoji);
  };

  return (
    <div
      className={`${styles.bar} ${size === 'sm' ? styles.sm : ''}`}
      data-testid="reaction-bar"
    >
      {visible.map((r) => {
        // Клик по чужой emoji при исчерпанном лимите добавил бы 4-ю — блокируем.
        const chipBlocked = !canReact || (!r.reactedByMe && atLimit);
        return (
          <Tooltip key={r.emoji} title={r.userNames.join(', ')}>
            <span
              className={`${styles.chip} ${r.reactedByMe ? styles.chipActive : ''} ${
                chipBlocked ? styles.chipDisabled : ''
              }`}
              role="button"
              tabIndex={chipBlocked ? -1 : 0}
              data-testid={`reaction-chip-${r.emoji}`}
              onClick={() => {
                if (chipBlocked) return;
                handleToggle(r.emoji);
              }}
              onKeyDown={(e) => {
                if (chipBlocked) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggle(r.emoji);
                }
              }}
            >
              <span>{r.emoji}</span>
              <span className={styles.count}>{r.count}</span>
            </span>
          </Tooltip>
        );
      })}

      {renderAdd && (
        <ReactionPicker reactions={reactions} onToggle={handleToggle} disabled={disabled}>
          <Tooltip title="Добавить реакцию">
            <button
              type="button"
              className={styles.addBtn}
              disabled={disabled}
              data-testid="reaction-add"
              aria-label="Добавить реакцию"
            >
              <SmileOutlined />
            </button>
          </Tooltip>
        </ReactionPicker>
      )}
    </div>
  );
}
